import { s } from '@/theme/tokens';
import { Box, CopyIcon, IconButton, Surface, Text } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives/types';
import type { ReactElement, ReactNode } from 'react';

export type CodeOutputBlockProps = {
  value: string;
  label?: ReactNode;
  onCopy?: () => void;
  copyAriaLabel?: string;
  reserveCopySpace?: boolean;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
};

export const CodeOutputBlock = ({
  value,
  label,
  onCopy,
  copyAriaLabel = 'Copy output',
  reserveCopySpace = true,
  sx,
  contentSx,
}: CodeOutputBlockProps): ReactElement => {
  const hasHeaderLabel = Boolean(label);
  const hasCopyAction = Boolean(onCopy);
  const showHeaderRow = hasHeaderLabel;
  const copyIconTopInset = s('xs');
  const copyIconRightInset = s('xs');
  const resolvedSx: SxProps<Theme> = sx
    ? [{ backgroundColor: 'background.paper' }, ...(Array.isArray(sx) ? sx : [sx])]
    : { backgroundColor: 'background.paper' };
  const resolvedContentSx: SxProps<Theme> = contentSx
    ? [
        {
          whiteSpace: 'pre-wrap',
          fontFamily: (theme) => theme.typography.mono.fontFamily,
          pr: !showHeaderRow && hasCopyAction && reserveCopySpace ? s('4xl') : undefined,
        },
        ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
      ]
    : {
        whiteSpace: 'pre-wrap',
        fontFamily: (theme) => theme.typography.mono.fontFamily,
        pr: !showHeaderRow && hasCopyAction && reserveCopySpace ? s('4xl') : undefined,
      };

  return (
    <Surface padding="none" border sx={resolvedSx}>
      <Box
        sx={{
          px: s('lg'),
          pt: showHeaderRow ? s('lg') : s('md'),
          pb: s('md'),
          position: 'relative',
        }}
      >
        {showHeaderRow ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: s('none'),
              marginBottom: s('md'),
              pr: hasCopyAction ? s('4xl') : undefined,
            }}
          >
            {label ? (
              <Text role="label" color="textSecondary" sx={{ lineHeight: 1 }}>
                {label}
              </Text>
            ) : null}
          </Box>
        ) : null}
        {hasCopyAction ? (
          <IconButton
            size="medium"
            aria-label={copyAriaLabel}
            onClick={onCopy}
            sx={{ position: 'absolute', top: copyIconTopInset, right: copyIconRightInset }}
          >
            <CopyIcon />
          </IconButton>
        ) : null}
        <Text role="body" sx={resolvedContentSx}>
          {value}
        </Text>
      </Box>
    </Surface>
  );
};
